import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';

const FeatureIcon = ({ children }) => (
  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border border-white/10 bg-white/[0.06] text-[#d2c0ff] shadow-[inset_0_1px_0_rgba(255,255,255,0.12)]">
    <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      {children}
    </svg>
  </div>
);

const Features = () => {
  const { t } = useLanguage();
  const features = t('features.items');
  const featureList = Array.isArray(features) ? features : [];
  const featured = featureList.slice(0, 3);
  const supporting = featureList.slice(3);

  const icons = [
    <path key="dashboard" strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.7} d="M4 5.5h16M4 12h7M4 18.5h16M15 9h5v6h-5z" />,
    <path key="ai" strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.7} d="M12 3l1.7 4.4L18 9l-4.3 1.6L12 15l-1.7-4.4L6 9l4.3-1.6L12 3zM5 16l.8 2.1L8 19l-2.2.9L5 22l-.8-2.1L2 19l2.2-.9L5 16zM19 14l1 2.6 2 .7-2 .7-1 2.6-1-2.6-2-.7 2-.7 1-2.6z" />,
    <path key="chart" strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.7} d="M4 19V5m0 14h16M8 16v-5m4 5V8m4 8v-3m3-8-4.5 4.5-3-3L7 11" />,
    <path key="recurring" strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.7} d="M17 7h3V4m0 3-3.2-3.2A7.5 7.5 0 005 7m2 10H4v3m0-3 3.2 3.2A7.5 7.5 0 0019 17" />,
    <path key="accounts" strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.7} d="M3 9h18M5 9v8m4-8v8m6-8v8m4-8v8M3 20h18M12 3l9 4H3l9-4z" />,
    <path key="alert" strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.7} d="M12 4v3m0 13a2 2 0 002-2h-4a2 2 0 002 2zm6-5H6l1.4-2V10a4.6 4.6 0 019.2 0v3L18 15z" />,
  ];

  return (
    <section id="caracteristicas" className="relative scroll-mt-28 overflow-hidden bg-black py-24 text-[#fffefa] sm:py-28">
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent" />
      <div className="absolute right-0 top-20 h-72 w-72 rounded-full bg-[#0a84ae]/10 blur-3xl" />

      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid gap-8 lg:grid-cols-[1.12fr_0.88fr] lg:items-end">
          <div className="max-w-4xl">
            <div className="mb-5 text-xs font-medium uppercase text-[#0a84ae]">{t('features.eyebrow')}</div>
            <h2 className="text-4xl font-normal leading-tight tracking-normal text-white sm:text-5xl lg:text-[56px]">
              {t('features.title1')}{' '}
              <span className="text-[#d2c0ff]">{t('features.titleHighlight')}</span>{' '}
              {t('features.title2')}
            </h2>
          </div>
          <p className="max-w-2xl text-lg leading-8 text-[#b7b5c2] lg:pb-1">
            {t('features.subtitle')}
          </p>
        </div>

        <div className="mt-16 space-y-5">
          <article className="relative overflow-hidden rounded-[28px] border border-white/10 bg-[#111116] shadow-[inset_0_1px_0_rgba(255,255,255,0.12),0_28px_90px_rgba(0,0,0,0.42)]">
            <div className="pointer-events-none absolute -left-24 top-0 h-80 w-80 rounded-full bg-[#7f49f5]/20 blur-3xl" />
            <div className="pointer-events-none absolute -right-20 bottom-0 h-64 w-64 rounded-full bg-[#0a84ae]/10 blur-3xl" />

            <div className="relative grid lg:grid-cols-[0.78fr_1.22fr]">
              <div className="flex flex-col justify-between border-b border-white/10 p-8 sm:p-10 lg:min-h-[520px] lg:border-b-0 lg:border-r lg:p-12">
                <div>
                  <div className="text-xs font-medium uppercase tracking-[0.16em] text-[#d2c0ff]">01—03 · FARM</div>
                  <h3 className="mt-6 max-w-lg text-3xl font-normal leading-tight text-white sm:text-4xl">
                    {t('features.featuredTitle')}
                  </h3>
                </div>
                <div className="mt-12 flex items-center gap-4 text-xs uppercase tracking-[0.14em] text-white/35">
                  <span className="h-px flex-1 bg-gradient-to-r from-white/20 to-transparent" />
                  <span>FARM 2.0</span>
                </div>
              </div>

              <div className="space-y-2 p-4 sm:p-6 lg:p-8">
                {featured.map((feature, index) => (
                  <div
                    key={feature.title}
                    className="grid gap-5 rounded-2xl border border-transparent p-5 transition-colors duration-300 hover:border-white/10 hover:bg-white/[0.035] sm:grid-cols-[auto_1fr] sm:p-6"
                  >
                    <FeatureIcon>{icons[index]}</FeatureIcon>
                    <div>
                      <div className="flex items-start justify-between gap-4">
                        <h4 className="text-xl font-medium text-white">{feature.title}</h4>
                        <span className="pt-1 text-xs text-white/30">0{index + 1}</span>
                      </div>
                      <p className="mt-2 max-w-xl leading-7 text-[#b7b5c2]">{feature.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </article>

          <div className="grid gap-5 md:grid-cols-3">
            {supporting.map((feature, index) => (
              <article
                key={feature.title}
                className="group flex min-h-[300px] flex-col rounded-[24px] border border-white/10 bg-[#0b0b0f] p-7 shadow-[inset_0_1px_0_rgba(255,255,255,0.09)] transition-all duration-300 hover:-translate-y-1 hover:border-[#d2c0ff]/30 hover:bg-[#101015]"
              >
                <div className="flex items-start justify-between">
                  <FeatureIcon>{icons[index + 3]}</FeatureIcon>
                  <span className="text-xs text-white/30">0{index + 4}</span>
                </div>
                <div className="mt-auto pt-12">
                  <h3 className="text-xl font-normal text-white">
                    {feature.title}
                  </h3>
                  <p className="mt-3 text-base leading-7 text-[#b7b5c2]">
                    {feature.description}
                  </p>
                </div>
              </article>
            ))}
          </div>
        </div>

        <div className="mt-14 flex justify-center">
          <button
            onClick={() => {
              const element = document.getElementById('capturas');
              if (element) element.scrollIntoView({ behavior: 'smooth' });
            }}
            className="inline-flex min-h-[48px] items-center gap-3 rounded-full border border-white/10 bg-white/[0.04] px-6 py-3 text-sm font-medium text-[#fffefa] transition-all duration-200 hover:border-[#d2c0ff]/30 hover:bg-white/[0.07] hover:text-[#d2c0ff]"
          >
            <span>{t('features.cta')}</span>
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0-7-7m7 7V3" />
            </svg>
          </button>
        </div>
      </div>
    </section>
  );
};

export default Features;
